// Phase 5.6 — flower size ladder. Mirrors backend `FLOWER_SIZE_LADDER`.
// `eighths` is the stock-deduction multiplier for each variant (stock is
// tracked in 3.5g eighths). Keep this list in lock-step with server.py.

export const FLOWER_SIZES = [
  { key: '3.5g', label: '3.5g', eighths: 1, price: 30 },
  { key: '7g',   label: '7g',   eighths: 2, price: 45 },
  { key: '14g',  label: '14g',  eighths: 4, price: 70 },
  { key: '28g',  label: '28g',  eighths: 8, price: 130 },
];

// Default selection when a flower card opens.
export const DEFAULT_FLOWER_SIZE = FLOWER_SIZES[0];

// Phase 5.9.3/4 — return the flower size ladder with per-product + per-region
// overrides applied. `product.region_prices[region].flower_prices` takes priority,
// then `product.flower_prices` (legacy global), then the default ladder.
//
// Feb 2026 fix — when `region` is null/undefined BUT the product has configured
// `region_prices`, fall back to the MIN price per-size across all configured
// regions instead of the system default. Without this, anyone browsing without
// a region (logged-out catalog peek, SA on the cross-region view, dispatcher
// looking at someone else's region) sees the stale $30/$45/$70/$130 defaults
// even after the operator customized the product's ladder.
export function flowerSizesForProduct(product, region = null) {
  const rp = (product?.region_prices || {})[region] || {};
  const regional = rp.flower_prices || {};
  const legacy = product?.flower_prices || {};

  // Cross-region MIN map — built once per call, only used when the requested
  // region didn't have a specific override for that size.
  const allRegionPrices = product?.region_prices || {};
  const crossRegionMin = {};
  for (const r of Object.keys(allRegionPrices)) {
    const fp = (allRegionPrices[r] || {}).flower_prices || {};
    for (const [k, v] of Object.entries(fp)) {
      if (typeof v === 'number' && v > 0) {
        crossRegionMin[k] = crossRegionMin[k] == null ? v : Math.min(crossRegionMin[k], v);
      }
    }
  }

  return FLOWER_SIZES.map((s) => {
    const r = regional[s.key];
    const l = legacy[s.key];
    const x = crossRegionMin[s.key];
    let price = s.price;
    if (typeof r === 'number' && r > 0) price = r;
    else if (typeof l === 'number' && l > 0) price = l;
    else if (typeof x === 'number' && x > 0) price = x; // any-region override beats system default
    return { ...s, price };
  });
}

// Auto-derive 3 effect chips from `strain_type` when product.effects is empty.
// Admin can later set product.effects directly to override.
const EFFECTS_BY_STRAIN = {
  sativa: ['Uplifted', 'Focused', 'Creative'],
  indica: ['Sleepy', 'Relaxed', 'Mellow'],
  hybrid: ['Happy', 'Relaxed', 'Creative'],
};

export function effectsForProduct(product) {
  if (Array.isArray(product?.effects) && product.effects.length > 0) {
    return product.effects.slice(0, 3);
  }
  const key = String(product?.strain_type || '').toLowerCase();
  return EFFECTS_BY_STRAIN[key] || [];
}

// Tiny emoji decoration for chips. Keeps tone friendly without leaning on icons.
const EFFECT_EMOJI = {
  Uplifted: '☀️', Focused: '💡', Creative: '🎨',
  Sleepy: '😴',  Relaxed: '🧘', Mellow: '🌙',
  Happy: '😊',
};
export function effectEmoji(name) { return EFFECT_EMOJI[name] || ''; }
